/**
 * MATAFOME — CLIENTE DE API CENTRAL (CONFIGURAÇÃO, SESSÃO E FETCH SEGURO)
 */

// API & WebSocket Service para o MataFome Desktop Multi-Tenant
// VPS Oficial na Nuvem como padrão definitivo
const DEFAULT_CLOUD_API = 'https://omatafome.com.br';

export function getApiBase() {
    // Se acessado diretamente pelo navegador via web na VPS em produção, usa a própria origem atual
    if (typeof window !== 'undefined' && window.location && window.location.origin && 
        !window.location.origin.startsWith('file:') && 
        (!window.location.origin.includes('localhost') || window.location.port === '3333') && 
        !window.location.origin.includes(':5000') &&
        !window.location.origin.includes(':8080')) {
        return window.location.origin;
    }
    const saved = localStorage.getItem('matafome_server_url');
    // Se estiver vazio ou apontando para domínio antigo da VPS anterior, migra automaticamente para o domínio oficial
    if (!saved || saved.includes('2.24.212.228') || saved.includes('pywssc') || saved.includes('wobezn.easypanel.host')) {
        localStorage.setItem('matafome_server_url', DEFAULT_CLOUD_API);
        return DEFAULT_CLOUD_API;
    }
    return saved;
}

export function setServerUrl(url) {
    let clean = (url || '').trim().replace(/\/$/, '');
    if (!clean) clean = DEFAULT_CLOUD_API;
    localStorage.setItem('matafome_server_url', clean);
    API_BASE = clean;
    return clean;
}

export let API_BASE = getApiBase();

// Expõe globalmente caso suporte precise diagnosticar via DevTools (Ctrl+Shift+I) sem expor na UI
if (typeof window !== 'undefined') {
    window.setServerUrl = setServerUrl;
    window.getApiBase = getApiBase;
}

export let currentToken = localStorage.getItem('matafome_token') || '';
export let currentStore = JSON.parse(localStorage.getItem('matafome_store') || 'null');
export let currentUser = JSON.parse(localStorage.getItem('matafome_user') || 'null');
let socketInstance = null;

export function getAuthHeaders() {
    const headers = { 'Content-Type': 'application/json' };
    if (currentToken) {
        headers['Authorization'] = `Bearer ${currentToken}`;
    }
    return headers;
}

export function saveCurrentUser(user) {
    currentUser = user;
    if (user) {
        localStorage.setItem('matafome_user', JSON.stringify(user));
    } else {
        localStorage.removeItem('matafome_user');
    }
}

export function getCurrentUser() {
    if (currentUser) return currentUser;
    if (currentStore) {
        return {
            id: currentStore.id,
            name: currentStore.name || 'Proprietário',
            role: 'owner'
        };
    }
    return null;
}

export function saveSession(token, store, user = null) {
    currentToken = token;
    currentStore = store;
    localStorage.setItem('matafome_token', token);
    localStorage.setItem('matafome_store', JSON.stringify(store));
    const effectiveUser = user || {
        id: store ? store.id : 'owner',
        name: store ? store.name : 'Proprietário',
        role: 'owner'
    };
    saveCurrentUser(effectiveUser);
}

export function clearSession() {
    currentToken = '';
    currentStore = null;
    currentUser = null;
    localStorage.removeItem('matafome_token');
    localStorage.removeItem('matafome_store');
    localStorage.removeItem('matafome_user');
    if (socketInstance) {
        socketInstance.disconnect();
        socketInstance = null;
    }
}

export async function logout() {
    try {
        if (currentToken) {
            await safeApiFetch('/api/auth/logout', {
                method: 'POST',
                headers: getAuthHeaders()
            });
        }
    } catch (err) {
        console.warn('[MataFome Auth] Falha ao notificar encerramento de sessão no servidor:', err.message);
    } finally {
        clearSession();
    }
}

export function getCurrentStore() {
    return currentStore;
}

// Requisição com tolerância a falhas: tenta a VPS; se inacessível, recorre ao servidor local
export async function safeApiFetch(urlPath, options = {}) {
    const target = `${API_BASE}${urlPath}`;
    const { timeout, headers, ...fetchOptions } = options;
    const timeoutMs = timeout || (urlPath.includes('/import-ai') || urlPath.includes('/ai') ? 90000 : 15000);

    // Injeta automaticamente o token Bearer e Content-Type se não especificado
    const authHeaders = getAuthHeaders();
    const finalHeaders = { ...authHeaders, ...(headers || {}) };

    const method = (fetchOptions.method || 'GET').toUpperCase();
    if (!fetchOptions.body) {
        if (['POST', 'PUT', 'PATCH'].includes(method)) {
            // Se for POST/PUT/PATCH e o Content-Type for JSON mas não tiver body, envia objeto vazio '{}'
            // para evitar erro Fastify/HTTP: "Body cannot be empty when content-type is set to 'application/json'"
            if (finalHeaders['Content-Type'] && finalHeaders['Content-Type'].includes('application/json')) {
                fetchOptions.body = JSON.stringify({});
            }
        } else {
            // Em requisições sem body (ex: GET, HEAD, DELETE), remove Content-Type desnecessário
            delete finalHeaders['Content-Type'];
            delete finalHeaders['content-type'];
        }
    }

    try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
        const res = await fetch(target, { ...fetchOptions, headers: finalHeaders, signal: controller.signal });
        clearTimeout(timeoutId);
        return res;
    } catch (err) {
        if (!API_BASE.includes('localhost') && !API_BASE.includes('127.0.0.1')) {
            try {
                const localRes = await fetch(`http://localhost:3333${urlPath}`, { ...fetchOptions, headers: finalHeaders });
                if (localRes) {
                    API_BASE = 'http://localhost:3333';
                    localStorage.setItem('matafome_server_url', 'http://localhost:3333');
                    return localRes;
                }
            } catch (_) {}
        }
        if (err.name === 'AbortError') {
            throw new Error('O tempo limite da requisição esgotou. A imagem pode ser muito pesada ou o servidor está ocupado.');
        }
        throw err;
    }
}

export function connectSocket(onNewOrder, onStatusUpdate, onSubscriptionUpdate) {
    const store = currentStore || JSON.parse(localStorage.getItem('matafome_store') || 'null');
    if (!store?.id) return null;
    if (typeof window === 'undefined' || !window.io) {
        console.warn('[MataFome Socket] Socket.io client não encontrado no window.');
        return null;
    }

    if (socketInstance) {
        socketInstance.disconnect();
    }

    socketInstance = window.io(getApiBase(), {
        transports: ['websocket', 'polling']
    });

    socketInstance.on('connect', () => {
        console.log('[MataFome Socket] Conectado ao servidor! ID:', socketInstance.id);
        socketInstance.emit('join_store', {
            storeId: store.id,
            accessKey: store.access_key,
            token: currentToken
        });
    });

    socketInstance.on('joined_store', (data) => {
        console.log(`[MataFome Socket] Sincronização em tempo real ativa na sala: ${data.room}`);
    });

    socketInstance.on('new_order', (payload) => {
        console.log('🔔 [MataFome Socket] NOVO PEDIDO RECEBIDO:', payload);
        if (onNewOrder) onNewOrder(payload);
    });

    socketInstance.on('order_status_updated', (payload) => {
        console.log('🔄 [MataFome Socket] Status de pedido atualizado:', payload);
        if (onStatusUpdate) onStatusUpdate(payload);
    });

    socketInstance.on('saas:subscription_updated', (payload) => {
        console.log('💎 [MataFome Socket] Assinatura SaaS atualizada:', payload);
        if (onSubscriptionUpdate) onSubscriptionUpdate(payload);
    });

    socketInstance.on('courier_queue_updated', (payload) => {
        console.log('🛵 [MataFome Socket] Fila de entregadores atualizada:', payload);
        if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('courier-queue-refresh', { detail: payload }));
        }
    });

    return socketInstance;
}

// ----------------------------------------------------
// RELATÓRIOS & DRE ANALÍTICO
// ----------------------------------------------------
